/**
 * @class QuantitySelector
 * @classdesc Quantity selectors are used to update the quantity of a product. Currently it can be used for the cart and product page.
 *
 * @extends Core.Component
 */
export default class QuantitySelector extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.buttonPlus = el.querySelector('[data-quantity-plus]');
    this.buttonMinus = el.querySelector('[data-quantity-minus]');
    this.quantityInput = el.querySelector('[data-quantity-input]');

    this.events.register(this.buttonPlus, 'click', e => this._inputChange(e));
    this.events.register(this.buttonMinus, 'click', e => this._inputChange(e));
    this.events.register(this.quantityInput, 'change', e => this._inputChange(e));

    this.inputTimer = null;

    this.state = {
      quantity: parseInt(this.quantityInput.value, 10)
    };
  }

  /**
   * Triggered when one of the quantity selector buttons or input is updated.
   * Dispatches an event called `quantity:update` which passes along `quantity` and `actionName` from the quantity selector.
   *
   * @param {Event} e - The event sent by the form being updated.
   * @private
   */
  _inputChange(e) {
    e.preventDefault();
    clearInterval(this.inputTimer);

    const currentTarget = e.target;
    const startValue = parseInt(this.quantityInput.value, 10);
    const min = parseInt(this.quantityInput.min, 10);
    const max = parseInt(this.quantityInput.max, 10);
    
    if (currentTarget.type === 'number') {
      if (!isNaN(max) && startValue > max) {
        const exceedEvent = new CustomEvent('quantity:exceed', { detail: { max: max } });
        this.el.dispatchEvent(exceedEvent);
        this.quantityInput.value = this.state.quantity;
        return;

      } else if (!isNaN(min) && startValue < min) {
        this.quantityInput.value = this.state.quantity;
        return;
      }

    } else if (currentTarget.name === 'minus') {
      if (startValue === min + 1) {
        this.quantityInput.value = min;
      } else {
        this.quantityInput.stepDown();
      }

    } else if (currentTarget.name === 'plus') {
      if (startValue === max) {
        const exceedEvent = new CustomEvent('quantity:exceed', { detail: { max: max } });
        this.el.dispatchEvent(exceedEvent);
      } else {
        this.quantityInput.stepUp();
      }
    }

    const newValue = parseInt(this.quantityInput.value, 10);

    this.inputTimer = setTimeout(() => {
      // Fire off event to submit the new params to the parent section
      if (
        startValue !== newValue || 
        newValue === 0 || 
        newValue === max || 
        currentTarget.type === 'number'
      ) {
        this.setState({
          quantity: newValue
        });
        const updateEvent = new CustomEvent('quantity:update', { detail: { quantity: newValue, actionName: currentTarget.name } });
        this.el.dispatchEvent(updateEvent);
      }
    }, 500);
  }

  /**
   * Sets focus on targeted element
   *
   * @param {Object} name - The element to focus on.
   */
  focus(name = null) {
    if (name) {
      this.el.querySelector(`[name="${name}"]`).focus();
    } else {
      this.quantityInput.focus();
    }
  }

  /**
   * Returns value of quantity input
   *
   */
  getValue() {
    return this.quantityInput.value;
  }
}
